/**
 * The pure INV-2 decision function (PX-W6.5) — the ONE place "may this form
 * be saved, and with what key material?" is decided.
 *
 * Why this exists as a pure function with its own suite: the backend stores
 * the PUT body's `apiKey` VERBATIM when `useOwnKey=true` (no preserve-existing
 * arm in `LlmConfigBusinessService.enrichForSave`) and answers reads MASKED
 * (`""` absent · `"****"` ≤4 chars · `"***-****-" + last4` otherwise). A form
 * that round-tripped the masked indicator — or saved `useOwnKey=true` with an
 * empty field — would CORRUPT the stored key: the masked text becomes the
 * new key, or the real key is silently blanked. The five branches below make
 * both classes of defect structurally unshippable, and
 * `llmConfigContract.test.ts` pins the server literals this guards against.
 *
 * The form layer's `newKey` (the field the user types) is stripped before
 * the payload leaves this function — `apiKey` on the returned payload is the
 * ONLY secret carrier.
 */

import type { LlmConfigInput } from './types'

/** The form's own shape: config fields + the key the user is typing. */
export interface LlmKeyForm {
  agentType: string
  provider: string
  model: string
  maxTokens: number
  temperature: number
  useOwnKey: boolean
  /** The key input's live value — ALWAYS starts empty on load (INV-2). */
  newKey: string
}

/** Why a save was refused. The page maps each reason to an i18n message. */
export type LlmKeyRefusal =
  | 'agent-type-required'
  | 'key-required'
  | 'masked-key-rejected'

export type LlmKeyDecision =
  | { ok: true; payload: LlmConfigInput }
  | { ok: false; reason: LlmKeyRefusal }

/**
 * Decide a save.
 *
 *  1. A blank `agentType` is refused (mirrors the server's own
 *     `llm-config.agent-type.required` — but refusing client-side keeps the
 *     user's typed key material in the form instead of burning it on a 400).
 *  2. `useOwnKey=false` saves with `apiKey: ''` — the server clears the
 *     stored key; the payload carries NO key material.
 *  3. `useOwnKey=true` with an empty key input is REFUSED: the store keeps
 *     the body's verbatim key, so "replace" demands explicit re-entry.
 *  4. A value in the masked read form (`****`, or anything starting with the
 *     `***-****-` prefix) is REFUSED: it is the indicator echoed back, never
 *     a key.
 *  5. Otherwise the payload carries the typed key and no `newKey`.
 */
export function resolveLlmKeySubmission(form: LlmKeyForm): LlmKeyDecision {
  if (form.agentType.trim() === '') {
    return { ok: false, reason: 'agent-type-required' }
  }
  if (!form.useOwnKey) {
    return {
      ok: true,
      payload: {
        agentType: form.agentType.trim(),
        provider: form.provider,
        model: form.model,
        useOwnKey: false,
        apiKey: '',
        maxTokens: form.maxTokens,
        temperature: form.temperature
      }
    }
  }
  const key = form.newKey.trim()
  if (key === '') {
    return { ok: false, reason: 'key-required' }
  }
  if (key === '****' || key.startsWith('***')) {
    return { ok: false, reason: 'masked-key-rejected' }
  }
  return {
    ok: true,
    payload: {
      agentType: form.agentType.trim(),
      provider: form.provider,
      model: form.model,
      useOwnKey: true,
      apiKey: key,
      maxTokens: form.maxTokens,
      temperature: form.temperature
    }
  }
}
